import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { FieldValues, useController, useFormContext } from 'react-hook-form';

import { ExternalContentSelectDrawer } from 'external/forms/Field/ExternalContentSelectorField/ExternalContentSelectDrawer';
import {
    IExternalContentListItemFragment,
    IExternalContentsSelectorQueryVariables,
    ExternalContentListItemFragmentDoc,
} from 'graphql/types';
import { ExternalContentListItem } from 'external/components/ExternalContentListItem';
import { useExternalContentList } from 'external/hooks';
import {
    getExternalContentSelectStatus,
    isExternalContentAdded,
    isExternalContentSelected,
} from 'external/utils/externalContentSelect';
import { AddButton } from 'common/components/Button/AddButton';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    isEditable?: boolean;
    label?: string;
    name: string;
    noResultsLabel?: string;
}

export const ExternalContentSelectorField = <TFieldValues extends FieldValues>({
    control: formControl,
    isEditable = true,
    label,
    name,
    noResultsLabel,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedExternalContents, setSelectedExternalContents] = useState<
        IExternalContentListItemFragment[]
    >([]);
    const [externalContentQueryVariables, setExternalContentQueryVariables] =
        useState<IExternalContentsSelectorQueryVariables | undefined>();
    const client = useApolloClient();

    const {
        externalContents,
        loading: externalContentsLoading,
        externalContentsCount,
    } = useExternalContentList(externalContentQueryVariables);

    const control = asTypedControl<string[]>(name, formControl);

    const { setValue } = useFormContext();
    const {
        field: { value: addedExternalContentIds },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
    });

    const addedExternalContents = useMemo(() => {
        // Get external content objects from cache based on the external content ids set in the form
        const externalContentsList =
            addedExternalContentIds
                ?.map((addedExternalContentId) => {
                    try {
                        return client.cache.readFragment<IExternalContentListItemFragment>(
                            {
                                id: `ExternalContent:${addedExternalContentId}`,
                                fragment: ExternalContentListItemFragmentDoc,
                                fragmentName: 'ExternalContentListItem',
                            }
                        );
                    } catch {
                        return null;
                    }
                })
                .filter(Boolean) || [];

        return externalContentsList as IExternalContentListItemFragment[];
    }, [addedExternalContentIds, client.cache]);

    const handleDrawerOpen = (open: boolean) => {
        if (!open) setSelectedExternalContents([]);

        setDrawerOpen(open);
    };

    const handleSelectExternalContent = (
        externalContent: IExternalContentListItemFragment
    ) => {
        const newExternalContents = [
            ...selectedExternalContents,
            externalContent,
        ].sort((a, b) => (a.title > b.title ? 1 : -1));

        setSelectedExternalContents(newExternalContents);
    };

    const handleRemoveExternalContent = (
        externalContent: IExternalContentListItemFragment
    ) => {
        const { isSelected } = getExternalContentSelectStatus(
            selectedExternalContents,
            addedExternalContents,
            externalContent
        );

        if (isSelected) {
            const newSelectedExternalContent = selectedExternalContents.filter(
                (selectedExternalContent) =>
                    selectedExternalContent.id !== externalContent.id
            );

            setSelectedExternalContents(newSelectedExternalContent);

            return;
        }

        setValue(
            name,
            addedExternalContents
                .filter(
                    (addedExternalContent) =>
                        addedExternalContent.id !== externalContent.id
                )
                .map((externalContent) => externalContent.id),
            { shouldDirty: true }
        );
    };

    const handleAddExternalContents = () => {
        setValue(
            name,
            [
                ...(addedExternalContentIds || []),
                ...selectedExternalContents.map(
                    (selectedExternalContent) => selectedExternalContent.id
                ),
            ],
            { shouldDirty: true }
        );

        setSelectedExternalContents([]);

        handleDrawerOpen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {isEditable && (
                    <AddButton
                        aria-label={translate('addExternalContentsTitle')}
                        error={
                            !!error?.message && isTouched
                                ? error.message
                                : undefined
                        }
                        onClick={() => handleDrawerOpen(true)}
                    >
                        {label || translate('addExternalContentsTitle')}
                    </AddButton>
                )}

                {!!addedExternalContents.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: isEditable ? 2 : 0,
                        }}
                    >
                        {addedExternalContents.map((addedExternalContent) => (
                            <ExternalContentListItem
                                addButton={isEditable}
                                added={isExternalContentAdded(
                                    addedExternalContents,
                                    addedExternalContent
                                )}
                                externalContent={addedExternalContent}
                                key={`added-${addedExternalContent.id}`}
                                selected={isExternalContentSelected(
                                    selectedExternalContents,
                                    addedExternalContent
                                )}
                                onClick={(
                                    externalContent: IExternalContentListItemFragment
                                ) => {
                                    if (!isEditable) return;

                                    handleRemoveExternalContent(
                                        externalContent
                                    );
                                }}
                            />
                        ))}
                    </Box>
                )}

                {!isEditable && !addedExternalContents.length && (
                    <Box>
                        <Typography>
                            {noResultsLabel ||
                                translate('noOptionsText.externalContents')}
                        </Typography>
                    </Box>
                )}
            </Box>

            <ExternalContentSelectDrawer
                addedExternalContents={addedExternalContents}
                externalContents={externalContents}
                externalContentsCount={externalContentsCount || undefined}
                loading={externalContentsLoading}
                open={drawerOpen}
                selectedExternalContents={selectedExternalContents}
                onAddExternalContents={handleAddExternalContents}
                onDrawerOpen={handleDrawerOpen}
                onRemoveExternalContent={handleRemoveExternalContent}
                onSelectExternalContent={handleSelectExternalContent}
                onSetExternalContentQueryVariables={
                    setExternalContentQueryVariables
                }
            />
        </Box>
    );
};
